import { use, useCallback, useState } from 'react';

import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import FormDialog from '~components/dialogs/formDialog';
import ExportForm, {
	getExportFormatExtension,
	type ExportFormat,
	type ExportStatus,
} from '~components/forms/exportForm';
import { queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { ResourceGroupService } from '~features/resourceGroups';
import useAuthorize from '~hooks/useAuthorize';
import { OffsetPagedResults } from '~interfaces';
import DeviceListType from '~interfaces/deviceListType';
import ExportService from '~services/exportService';
import { getService } from '~services/serviceFactory';

import Device from '../../../interfaces/device';
import DevicesDataGrid from '../components/dataGrids/devicesDataGrid';
import DeviceMultiAssignDialog from '../components/dialogs/deviceMultiAssignDialogs';
import SelectedDevicesContext from '../providers/selectedDevicesContext';

const organisationService = getService(OrganisationsService);
const resourceGroupService = getService(ResourceGroupService);
const exportService = getService(ExportService);

type UserAction = {
	type: 'assignDevices' | 'export';
	mutate?: KeyedMutator<OffsetPagedResults<Device>>;
	clearSelection?: () => void;
} | null;

interface Props {
	deviceListType: DeviceListType;
}

const DevicesOverviewContainer = ({ deviceListType }: Props) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const selectedDevicesContext = use(SelectedDevicesContext);

	const [userAction, setUserAction] = useState<UserAction | null>(null);
	const [exportStatus, setExportStatus] = useState<ExportStatus>('selecting');
	const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null);
	const [showModalLoader, setShowModalLoader] = useState(false);
	const [searchParams] = useSearchParams();
	const allowsExporting = useAuthorize().authorize([
		UserRoleEnum.SuperAdmin,
		UserRoleEnum.Support,
		UserRoleEnum.Manufacturing,
		UserRoleEnum.ClientAdmin,
	]);

	const clearUserAction = () => {
		setUserAction(null);
		setTimeout(() => {
			setExportStatus('selecting');
			setSelectedFormat(null);
		}, 300);
	};

	const handleAssign = (
		mutate: KeyedMutator<OffsetPagedResults<Device>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'assignDevices',
			clearSelection,
			mutate: mutate,
		});
	};

	const handleAssignDevices = (
		organisationId: string,
		resourceGroupId: string | undefined,
		closeDialog: () => void
	) => {
		if (userAction?.type !== 'assignDevices') return;

		const deviceIds =
			selectedDevicesContext?.singleDevice ?
				[selectedDevicesContext?.singleDevice.id]
			:	(selectedDevicesContext?.selectedDevices?.flatMap((value) => value.id) ?? []);
		const isSingleDevice = deviceIds.length == 1;

		const successValue =
			isSingleDevice ?
				t('deviceAssignedSuccesfully', { ns: 'toast' })
			:	t('devicesAssignedSuccesfully', { ns: 'toast' });
		const failedValue =
			isSingleDevice ?
				t('deviceFailedToAssign', { ns: 'toast' })
			:	t('devicesFailedToAssign', { ns: 'toast' });

		if (!deviceIds) {
			enqueueSnackbar(failedValue, { variant: 'error' });
			return;
		}

		setShowModalLoader(true);
		organisationService
			.assignDevicesToOrganisation(deviceIds, organisationId)
			.then(() => {
				if (resourceGroupId) {
					resourceGroupService
						.assignDevicesToResourceGroup(deviceIds, resourceGroupId, organisationId)
						.then(() => {
							enqueueSnackbar(successValue, { variant: 'success' });
							cleanUpAfterDialog();
						})
						.catch((_reason) => {
							enqueueSnackbar(failedValue, { variant: 'error' });
						});
				} else {
					enqueueSnackbar(successValue, { variant: 'success' });
					cleanUpAfterDialog(closeDialog);
				}
			})
			.catch((_reason) => {
				enqueueSnackbar(t('failedToAssignToOrganisation', { ns: 'errors' }), { variant: 'error' });
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const cleanUpAfterDialog = (closeDialog?: () => void) => {
		setUserAction(null);
		userAction?.mutate?.();
		userAction?.clearSelection?.();
		selectedDevicesContext.updateSingleDevice(undefined);
		closeDialog?.();
	};

	function triggerBlobDownload(blob: Blob, fileName: string) {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	const handleExport = useCallback(
		async (format: ExportFormat) => {
			setExportStatus('exporting');

			try {
				const search = searchParams.get(queryParameters.SearchQuery) ?? undefined;
				const model = searchParams.get(queryParameters.DeviceModelName) ?? undefined;
				const resourceGroupId = searchParams.get(queryParameters.ResourceGroupId) ?? undefined;
				const clientId = searchParams.get(queryParameters.OrganisationId) ?? undefined;

				const blob = await exportService.exportDevices({
					format,
					search,
					model,
					resourceGroupId,
					clientId,
				});

				const fileName = `devices-export_${dayjs().format('DD-MM-YYYY_HH-mm')}${getExportFormatExtension(format)}`;
				triggerBlobDownload(blob, fileName);

				enqueueSnackbar(t('exportGeneratedSuccessfully', { ns: 'toast' }), { variant: 'success' });
			} catch (error) {
				console.error('Export failed:', error);
				enqueueSnackbar(t('exportFailedToGenerate', { ns: 'toast' }), { variant: 'error' });
			} finally {
				clearUserAction();
			}
		},
		[searchParams, enqueueSnackbar, t]
	);
	return (
		<>
			{/* Key helps with render issues if there are more tables on the same page (like devices with a tab layout) */}
			<DevicesDataGrid
				key={'devices' + deviceListType}
				showExportButton={allowsExporting}
				listType={deviceListType}
				showTitle={false}
				enableDeleteDevice
				enableEditDevice
				enableMultiSelect={deviceListType == 'unassigned'}
				enableAssignDevice={deviceListType == 'unassigned'}
				includeOrganisation={deviceListType == 'assignedInternal'}
				includeResourceGroup={deviceListType == 'assignedInternal'}
				onAssignDevices={handleAssign}
				handleExportButtonClicked={() => setUserAction({ type: 'export' })}
			/>
			<DeviceMultiAssignDialog
				open={userAction?.type === 'assignDevices'}
				loading={showModalLoader}
				onClose={clearUserAction}
				onFinish={handleAssignDevices}
			/>
			<FormDialog
				open={userAction?.type === 'export'}
				title={
					t('export', { ns: 'actions' }) +
					' ' +
					t('device.labels.plural', { ns: 'entities' }).toLowerCase()
				}
				loading={exportStatus === 'exporting'}
				submitLabel={t('export', { ns: 'actions' })}
			>
				<ExportForm
					status={exportStatus}
					selectedFormat={selectedFormat}
					onFormatChange={setSelectedFormat}
					onSubmit={handleExport}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default DevicesOverviewContainer;
